import { Directive, forwardRef, inject, Injector, input } from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { CountryCode } from 'libphonenumber-js';

import { validatePhone } from '../validators/phone.validator';

/**
 * Validator directive for international phone numbers.
 * Uses libphonenumber-js for accurate validation.
 *
 * Validation timing:
 * - Initial validation: on blur only
 * - Re-validation after error: on input (clears instantly when valid)
 *
 * Usage:
 * ```html
 * <!-- Default country: Brazil -->
 * <input type="tel" [(ngModel)]="phone" appPhone #phoneControl="ngModel">
 *
 * <!-- Custom default country -->
 * <input type="tel" [(ngModel)]="phone" appPhone [phoneCountry]="'PT'" #phoneControl="ngModel">
 *
 * @if (phoneControl.errors?.['phone']) {
 *   <span>{{ getErrorMessage(phoneControl.errors['phone']) }}</span>
 * }
 * ```
 */
@Directive({
  selector: '[appPhone]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneValidatorDirective),
      multi: true,
    },
  ],
  host: {
    '(blur)': 'onBlur()',
    '(input)': 'onInput()',
  },
})
export class PhoneValidatorDirective implements Validator {
  /**
   * Default country code for phone validation.
   * Used when phone number doesn't start with + prefix.
   * Default: 'BR' (Brazil)
   */
  phoneCountry = input<CountryCode>('BR');

  private _injector = inject(Injector);
  private _ngControl: NgControl | null = null;

  private _hasBlurred = false;
  private _lastError: ValidationErrors | null = null;

  /**
   * Lazily resolve NgControl to avoid circular dependency.
   * NgControl depends on validators, and this directive is a validator.
   */
  private get ngControl(): NgControl | null {
    if (!this._ngControl) {
      try {
        this._ngControl = this._injector.get(NgControl, null);
      } catch {
        this._ngControl = null;
      }
    }
    return this._ngControl;
  }

  /**
   * Validator function called by Angular forms.
   */
  validate(control: AbstractControl): ValidationErrors | null {
    // Don't validate until user has blurred at least once
    if (!this._hasBlurred) {
      return null;
    }

    this._lastError = validatePhone(control.value, this.phoneCountry());
    return this._lastError;
  }

  /**
   * Handle blur event - trigger validation.
   */
  onBlur(): void {
    this._hasBlurred = true;

    // Trigger validation update
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
  }

  /**
   * Handle input event - re-validate if already has error (hybrid approach).
   */
  onInput(): void {
    if (this._hasBlurred && this._lastError && this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
  }
}
